<template>
  <div class="moments-card">
    <h4 class="moments-card__title">{{ $t('page.about.momentsCard.title') }}</h4>

    <!-- Reset button in top right corner -->
    <button class="moments-card__reset" @click="resetPhotos" title="Reset positions">
      <svg
        class="moments-card__reset-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    </button>

    <!-- Wire Grid Frame -->
    <div class="wire-grid" ref="wireGrid">
      <!-- Metal mesh grid pattern -->
      <div class="wire-grid__mesh"></div>

      <!-- Corner pins/screws -->
      <div class="wire-grid__pin wire-grid__pin--tl"></div>
      <div class="wire-grid__pin wire-grid__pin--tr"></div>
      <div class="wire-grid__pin wire-grid__pin--bl"></div>
      <div class="wire-grid__pin wire-grid__pin--br"></div>

      <!-- Photos container for centering -->
      <div class="wire-grid__photos" ref="photosContainer">
        <!-- Photos with clips -->
        <div
          v-for="(photo, index) in photos"
          :key="index"
          class="photo-frame"
          :class="[`photo-frame--${index + 1}`, { 'photo-frame--dragging': photo.isDragging }]"
          :style="{
            transform: `translate(${photo.x}px, ${photo.y}px) rotate(${photo.isDragging ? 0 : photo.rotation}deg)`,
            zIndex: photo.zIndex,
          }"
          @mousedown="startDrag($event, index)"
          @touchstart="startDrag($event, index)"
        >
          <div
            class="photo-frame__inner"
            :class="{ 'photo-frame__inner--animate': showAnimation }"
            :style="{ '--delay': `${index * 0.1}s` }"
          >
            <!-- Clip/Pinza - hidden when dragging -->
            <div
              class="photo-frame__clip"
              :class="[
                `photo-frame__clip--${photo.clipPosition || 'center'}`,
                { 'photo-frame__clip--hidden': photo.isDragging },
              ]"
            >
              <div class="photo-frame__clip-body"></div>
            </div>

            <!-- Photo -->
            <div class="photo-frame__photo">
              <!-- Loading skeleton -->
              <div v-if="!photo.loaded" class="photo-frame__skeleton">
                <div class="photo-frame__skeleton-shimmer"></div>
              </div>

              <img
                class="photo-frame__img"
                :class="{ 'photo-frame__img--loaded': photo.loaded }"
                :src="photo.src"
                :alt="photo.alt"
                draggable="false"
                loading="lazy"
                @load="onImageLoad(index)"
                @error="onImageError(index)"
              />
              <div class="photo-frame__shine"></div>
            </div>

            <!-- Handwritten label -->
            <span class="photo-frame__label">
              {{ photo.labelKey ? $t(photo.labelKey) : photo.label }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'

// Import local images
import behind1 from '@/assets/images/behind/behind-1.webp'
import behind2 from '@/assets/images/behind/behind-2.webp'
import behind3 from '@/assets/images/behind/behind-3.webp'
import behind4 from '@/assets/images/behind/behind-4.webp'
import behind5 from '@/assets/images/behind/behind-5.webp'
import behind6 from '@/assets/images/behind/behind-6.webp'
import behind7 from '@/assets/images/behind/behind-7.webp'
import behind8 from '@/assets/images/behind/behind-8.webp'

const wireGrid = ref(null)
const photosContainer = ref(null)
const showAnimation = ref(false)

const STORAGE_KEY = 'moments-photo-positions'

const initialPositions = [
  { x: -80, y: -40, rotation: -10, zIndex: 1 },
  { x: 0, y: -50, rotation: 4, zIndex: 2 },
  { x: 80, y: -30, rotation: -6, zIndex: 3 },
  { x: -90, y: 40, rotation: 7, zIndex: 4 },
  { x: 10, y: 30, rotation: -3, zIndex: 8 },
  { x: 90, y: 40, rotation: 8, zIndex: 5 },
  { x: -40, y: 90, rotation: -7, zIndex: 6 },
  { x: 60, y: 90, rotation: 5, zIndex: 7 },
]

// Load saved positions from localStorage
const loadSavedPositions = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.warn('Could not load saved photo positions')
  }
  return null
}

// Save positions to localStorage
const savePositions = () => {
  try {
    const positions = photos.map((photo) => ({
      x: photo.x,
      y: photo.y,
      rotation: photo.rotation,
      zIndex: photo.zIndex,
    }))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions))
  } catch (e) {
    console.warn('Could not save photo positions')
  }
}

const savedPositions = loadSavedPositions()

const photos = reactive([
  {
    src: behind1,
    alt: 'Coding',
    label: '🏖️🌞',
    clipPosition: 'left',
    ...(savedPositions?.[0] || initialPositions[0]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind2,
    alt: 'Coffee',
    label: '2020 👨‍💻',
    clipPosition: 'right',
    ...(savedPositions?.[1] || initialPositions[1]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind3,
    alt: 'Adventure',
    label: 'HotWheels ❤️‍🔥',
    clipPosition: 'right',
    ...(savedPositions?.[2] || initialPositions[2]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind4,
    alt: 'Music',
    label: '2021 💖',
    clipPosition: 'left',
    ...(savedPositions?.[3] || initialPositions[3]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind5,
    alt: 'Gaming',
    label: '💖💖💖',
    clipPosition: 'center',
    ...(savedPositions?.[4] || initialPositions[4]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind6,
    alt: 'Workspace',
    label: 'Penny 😽',
    clipPosition: 'right',
    ...(savedPositions?.[5] || initialPositions[5]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind7,
    alt: 'Learning',
    labelKey: 'page.about.momentsCard.labels.alwaysLearning',
    label: 'Always learning 📚',
    clipPosition: 'left',
    ...(savedPositions?.[6] || initialPositions[6]),
    isDragging: false,
    loaded: false,
  },
  {
    src: behind8,
    alt: 'Team',
    labelKey: 'page.about.momentsCard.labels.dreamTeam',
    label: 'Dream Team 💜',
    clipPosition: 'center',
    ...(savedPositions?.[7] || initialPositions[7]),
    isDragging: false,
    loaded: false,
  },
])

// Image loading handlers
const onImageLoad = (index) => {
  photos[index].loaded = true
}

const onImageError = (index) => {
  // Still mark as loaded to remove skeleton, but you could show an error state
  photos[index].loaded = true
  console.warn(`Failed to load image at index ${index}`)
}

let isDragging = false
let currentIndex = -1
let startX = 0
let startY = 0
let initialX = 0
let initialY = 0
let maxZIndex = savedPositions ? Math.max(...savedPositions.map((p) => p.zIndex)) + 1 : 10

const dragConstraints = {
  minX: 0,
  maxX: 0,
  minY: 0,
  maxY: 0,
}

const startDrag = (e, index) => {
  e.preventDefault()
  isDragging = true
  currentIndex = index

  // Mark as dragging (hides clip)
  photos[index].isDragging = true

  // Bring to front
  maxZIndex++
  photos[index].zIndex = maxZIndex

  const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
  const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY

  startX = clientX
  startY = clientY
  initialX = photos[index].x
  initialY = photos[index].y

  // Cache constraints on start
  if (photosContainer.value) {
    const container = photosContainer.value.getBoundingClientRect()
    const maxOffsetX = container.width / 2 - 30
    const maxOffsetY = container.height / 2 - 40
    dragConstraints.minX = -maxOffsetX
    dragConstraints.maxX = maxOffsetX
    dragConstraints.minY = -maxOffsetY
    dragConstraints.maxY = maxOffsetY
  }

  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onDrag, { passive: false })
  document.addEventListener('touchend', stopDrag)
}

const onDrag = (e) => {
  if (!isDragging || currentIndex === -1) return
  e.preventDefault()

  const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
  const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY

  const deltaX = clientX - startX
  const deltaY = clientY - startY

  // Calculate new position
  let newX = initialX + deltaX
  let newY = initialY + deltaY

  // Use cached constraints
  newX = Math.max(dragConstraints.minX, Math.min(dragConstraints.maxX, newX))
  newY = Math.max(dragConstraints.minY, Math.min(dragConstraints.maxY, newY))

  photos[currentIndex].x = newX
  photos[currentIndex].y = newY
}

const stopDrag = () => {
  if (currentIndex !== -1) {
    photos[currentIndex].isDragging = false
    savePositions()
  }

  isDragging = false
  currentIndex = -1

  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
}

const resetPhotos = () => {
  photos.forEach((photo, index) => {
    photo.x = initialPositions[index].x
    photo.y = initialPositions[index].y
    photo.rotation = initialPositions[index].rotation
    photo.zIndex = initialPositions[index].zIndex
    photo.isDragging = false
  })
  maxZIndex = 10
  localStorage.removeItem(STORAGE_KEY)
}

onMounted(() => {
  setTimeout(() => {
    showAnimation.value = true
  }, 400)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onDrag)
  document.removeEventListener('touchend', stopDrag)
})
</script>

<style scoped>
/* ==========================================================================
   MOMENTS CARD - BEM Structure with Tailwind @apply
   ========================================================================== */

/* Block: moments-card */
.moments-card {
  @apply relative h-full min-h-[400px] p-4 flex flex-col overflow-hidden;
}

@screen md {
  .moments-card {
    @apply p-6;
  }
}

.moments-card__title {
  @apply text-xs uppercase tracking-wider mb-4 text-left;
  color: var(--color-text-muted);
}

.moments-card__reset {
  @apply absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center;
  @apply cursor-pointer opacity-60 transition-all duration-300 z-50;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border);
}

.moments-card__reset:hover {
  @apply opacity-100 -rotate-45;
  background: var(--color-electric-purple);
  border-color: var(--color-electric-purple);
}

.moments-card__reset-icon {
  @apply w-3.5 h-3.5;
  color: var(--color-text-secondary);
}

.moments-card__reset:hover .moments-card__reset-icon {
  @apply text-white;
}

/* Block: wire-grid */
.wire-grid {
  @apply relative flex-1 rounded-lg p-3 overflow-hidden;
  min-height: 280px;
  border: 3px solid #4a4a50;
}

.wire-grid__mesh {
  @apply absolute inset-0 rounded-md z-[1];
  background-image:
    linear-gradient(to right, rgba(120, 120, 130, 0.4) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(120, 120, 130, 0.4) 1px, transparent 1px);
  background-size: 24px 24px;
}

.wire-grid__mesh::before {
  content: '';
  @apply absolute inset-0;
  background-image: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.03) 1px,
    transparent 1px
  );
  background-size: 24px 24px;
  background-position: 12px 12px;
}

.wire-grid__pin {
  @apply absolute w-3 h-3 rounded-full z-[5];
  background: linear-gradient(135deg, #6a6a70 0%, #3a3a40 100%);
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.3);
}

.wire-grid__pin::before {
  content: '';
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full;
  background: #2a2a30;
}

.wire-grid__pin--tl {
  @apply top-1.5 left-1.5;
}
.wire-grid__pin--tr {
  @apply top-1.5 right-1.5;
}
.wire-grid__pin--bl {
  @apply bottom-1.5 left-1.5;
}
.wire-grid__pin--br {
  @apply bottom-1.5 right-1.5;
}

.wire-grid__photos {
  @apply absolute inset-5 flex items-center justify-center z-10;
}

/* Block: photo-frame */
.photo-frame {
  @apply absolute z-10 cursor-grab select-none touch-none;
  will-change: transform;
  transition:
    transform 0.3s ease,
    z-index 0s,
    box-shadow 0.2s ease;
}

.photo-frame__inner {
  opacity: 0;
}

.photo-frame__inner--animate {
  animation: photo-appear 0.6s ease-out both;
  animation-delay: var(--delay);
}

.photo-frame:hover:not(.photo-frame--dragging) {
  z-index: 20;
}

.photo-frame--dragging {
  @apply cursor-grabbing z-[100];
  transition: none;
}

.photo-frame--dragging .photo-frame__photo {
  @apply scale-[1.09];
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 10px 20px rgba(0, 0, 0, 0.2);
}

@keyframes photo-appear {
  0% {
    @apply opacity-0;
    transform: scale(0.5) translateY(-20px);
  }
  100% {
    @apply opacity-100;
    transform: none;
  }
}

.photo-frame__clip {
  @apply absolute -top-2 z-[15] transition-all duration-200;
}

/* Clip positions */
.photo-frame__clip--center {
  @apply left-1/2 -translate-x-1/2;
}

.photo-frame__clip--left {
  @apply left-2;
  transform: rotate(-8deg);
}

.photo-frame__clip--right {
  @apply right-2 left-auto;
  transform: rotate(8deg);
}

.photo-frame__clip--hidden {
  @apply opacity-0 scale-50;
}

.photo-frame__clip--hidden.photo-frame__clip--center {
  transform: translateX(-50%) translateY(-10px) scale(0.5);
}

.photo-frame__clip--hidden.photo-frame__clip--left {
  transform: rotate(-8deg) translateY(-10px) scale(0.5);
}

.photo-frame__clip--hidden.photo-frame__clip--right {
  transform: rotate(8deg) translateY(-10px) scale(0.5);
}

.photo-frame__clip-body {
  @apply relative rounded-t;
  width: 18px;
  height: 26px;
  background: linear-gradient(180deg, #e8d5c4 0%, #d4c4b0 50%, #c9b8a5 100%);
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.photo-frame__clip-body::before {
  content: '';
  @apply absolute top-0.5 left-0.5 right-0.5 h-1.5 rounded-sm;
  background: linear-gradient(90deg, #bfae9a 0%, #a89882 50%, #bfae9a 100%);
}

.photo-frame__clip-body::after {
  content: '';
  @apply absolute bottom-0.5 left-1/2 -translate-x-1/2 w-2 h-0.5 rounded-sm;
  background: rgba(0, 0, 0, 0.1);
}

.photo-frame__photo {
  @apply relative overflow-hidden transition-all duration-200 rounded;
  width: 100px;
  height: 120px;
  background: white;
  padding: 6px 6px 24px 6px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Skeleton loading state */
.photo-frame__skeleton {
  @apply absolute inset-0 rounded-sm overflow-hidden;
  top: 6px;
  left: 6px;
  right: 6px;
  bottom: 24px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%);
}

.photo-frame__skeleton-shimmer {
  @apply absolute inset-0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.photo-frame__img {
  @apply w-full object-cover select-none pointer-events-none rounded-sm;
  height: calc(100% - 12px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-frame__img--loaded {
  opacity: 1;
}

.photo-frame__shine {
  @apply absolute inset-0 pointer-events-none;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    transparent 50%,
    transparent 100%
  );
}

.photo-frame__label {
  @apply absolute left-0 right-0 text-center pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis px-1;
  bottom: 5px;
  font-size: 11px;
  color: #555;
  font-family: 'Comic Sans MS', 'Marker Felt', cursive, sans-serif;
}

/* ==========================================================================
   RESPONSIVE
   ========================================================================== */

@media (max-width: 768px) {
  .wire-grid {
    min-height: 300px;
  }

  .wire-grid__mesh {
    background-size: 20px 20px;
  }

  .wire-grid__mesh::before {
    background-size: 20px 20px;
    background-position: 10px 10px;
  }

  .wire-grid__pin {
    @apply w-2.5 h-2.5;
  }

  .wire-grid__pin::before {
    @apply w-0.5 h-0.5;
  }

  .photo-frame__photo {
    width: 85px;
    height: 105px;
    padding: 5px 5px 20px 5px;
  }

  .photo-frame__skeleton {
    top: 5px;
    left: 5px;
    right: 5px;
    bottom: 20px;
  }

  .photo-frame__label {
    font-size: 9px;
    bottom: 4px;
  }

  .photo-frame__clip-body {
    width: 14px;
    height: 18px;
  }
}

@media (max-width: 480px) {
  .wire-grid {
    min-height: 240px;
  }

  .wire-grid__mesh {
    background-size: 16px 16px;
  }

  .wire-grid__mesh::before {
    background-size: 16px 16px;
    background-position: 8px 8px;
  }

  .wire-grid__pin {
    @apply w-2 h-2;
  }

  .wire-grid__pin::before {
    @apply w-0.5 h-0.5;
  }

  .photo-frame__photo {
    width: 65px;
    height: 80px;
    padding: 4px 4px 16px 4px;
  }

  .photo-frame__skeleton {
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 16px;
  }

  .photo-frame__label {
    font-size: 7px;
  }

  .photo-frame__clip-body {
    width: 12px;
    height: 16px;
  }

  .photo-frame__clip {
    @apply -top-1.5;
  }
}

/* ==========================================================================
   DARK MODE
   ========================================================================== */

:root.dark .photo-frame__clip-body,
.dark .photo-frame__clip-body {
  background: linear-gradient(180deg, #8a7a6a 0%, #7a6a5a 50%, #6a5a4a 100%);
}

:root.dark .photo-frame__clip-body::before,
.dark .photo-frame__clip-body::before {
  background: linear-gradient(90deg, #5a4a3a 0%, #4a3a2a 50%, #5a4a3a 100%);
}

:root.dark .photo-frame__skeleton,
.dark .photo-frame__skeleton {
  background: linear-gradient(135deg, #3a3a40 0%, #4a4a50 50%, #3a3a40 100%);
}

:root.dark .photo-frame__skeleton-shimmer,
.dark .photo-frame__skeleton-shimmer {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 50%,
    transparent 100%
  );
}
</style>
